import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';

const ManualQuestionCreator = ({ onQuestionCreated, onClose }) => {
  const [formData, setFormData] = useState({
    text: '',
    options: ['', '', '', ''],
    answer: '',
    year: '',
    subject: '',
    topic: '',
    section: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({ ...prev, options: [...prev.options, ''] }));
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.text.trim()) {
      setError('Question text is required');
      return;
    }

    const filledOptions = formData.options.filter(opt => opt.trim());
    if (filledOptions.length < 2) {
      setError('At least 2 options are required');
      return;
    }

    if (!formData.answer.trim()) {
      setError('Correct answer is required');
      return;
    }

    const answerLetter = formData.answer.trim().toUpperCase();
    const answerIndex = answerLetter.charCodeAt(0) - 65; // A=0, B=1, etc.
    if (answerIndex < 0 || answerIndex >= filledOptions.length) {
      setError(`Answer must be a letter between A and ${String.fromCharCode(65 + filledOptions.length - 1)}`);
      return;
    }

    if (!formData.year || !formData.subject || !formData.section) {
      setError('Year, subject, and section are required');
      return;
    }

    try {
      setLoading(true);
      const questionData = {
        text: formData.text.trim(),
        options: filledOptions,
        answer: answerLetter,
        year: formData.year,
        subject: formData.subject,
        topic: formData.topic.trim() || null,
        section: formData.section
      };

      await api.post('/questions', questionData);
      
      // Reset form
      setFormData({
        text: '',
        options: ['', '', '', ''],
        answer: '',
        year: formData.year, // Keep year, subject, section for convenience
        subject: formData.subject,
        topic: '',
        section: formData.section
      });

      if (onQuestionCreated) {
        onQuestionCreated();
      }

      alert('Question created successfully!');
    } catch (err) {
      console.error('Error creating question:', err);
      setError(err.response?.data?.error || 'Failed to create question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Create New Question</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Question Text */}
          <div>
            <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 mb-2">
              Question Text *
            </label>
            <textarea
              id="questionText"
              value={formData.text}
              onChange={(e) => handleInputChange('text', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your question here..."
              required
            />
          </div>

          {/* Options */}
          <div>
            <label htmlFor="option-0" className="block text-sm font-medium text-gray-700 mb-2">
              Answer Options * (minimum 2)
            </label>
            <div className="space-y-2">
              {formData.options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-shrink-0 w-8 h-10 flex items-center justify-center bg-gray-100 rounded font-semibold text-gray-700">
                    {String.fromCharCode(65 + index)}
                  </div>
                  <input
                    id={`option-${index}`}
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Option ${String.fromCharCode(65 + index)}`}
                  />
                  {formData.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {formData.options.length < 6 && (
              <button
                type="button"
                onClick={addOption}
                className="mt-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium"
              >
                + Add Option
              </button>
            )}
          </div>

          {/* Correct Answer */}
          <div>
            <label htmlFor="correctAnswer" className="block text-sm font-medium text-gray-700 mb-2">
              Correct Answer *
            </label>
            <input
              id="correctAnswer"
              type="text"
              value={formData.answer}
              onChange={(e) => handleInputChange('answer', e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter letter (A, B, C, etc.)"
              maxLength={1}
              required
            />
          </div>

          {/* Metadata Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                Year/Class *
              </label>
              <select
                id="year"
                value={formData.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Year</option>
                <option value="shs1">SHS 1</option>
                <option value="shs2">SHS 2</option>
                <option value="shs3">SHS 3</option>
              </select>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                id="subject"
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Mathematics, English"
                required
              />
            </div>
          </div>

          {/* Metadata Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-2">
                Topic (Optional)
              </label>
              <input
                id="topic"
                type="text"
                value={formData.topic}
                onChange={(e) => handleInputChange('topic', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Algebra, Grammar"
              />
            </div>

            <div>
              <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-2">
                Section/Term *
              </label>
              <input
                id="section"
                type="text"
                value={formData.section}
                onChange={(e) => handleInputChange('section', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Term 1, Midterm"
                required
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Creating...' : 'Create Question'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ManualQuestionCreator.propTypes = {
  onQuestionCreated: PropTypes.func,
  onClose: PropTypes.func.isRequired
};

export default ManualQuestionCreator;
